import { FieldValues } from 'react-hook-form';
import { create } from 'zustand';
import {
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { SubmitHandler, XemploFormStore } from './xemplo-form-store.types';

export const useXemploFormStore = create<XemploFormStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        immer((set, get) => ({
          forms: {},
          formIdentifier: {},
          setFormValues: (formId: string, values: FieldValues) =>
            set((state) => {
              if (!state.forms[formId]) {
                state.forms[formId] = { values };
              } else {
                state.forms[formId].values = values;
              }
            }),

          setFormField: (formId: string, field: string, value: any) =>
            set((state) => {
              if (!state.forms[formId]) {
                state.forms[formId] = { values: { [field]: value } };
              } else {
                if (!state.forms[formId].values) {
                  state.forms[formId].values = {};
                }
                state.forms[formId].values[field] = value;
              }
            }),

          setFormSubmit: (formId: string, submit: SubmitHandler) =>
            set((state) => {
              if (!state.forms[formId]) {
                state.forms[formId] = { submit };
              } else {
                state.forms[formId].submit = submit;
              }
            }),

          getFormValues: (formId: string) => {
            return get().forms[formId]?.values;
          },

          getFormSubmit: (formId: string) => {
            return get().forms[formId]?.submit;
          },

          removeForm: (formId: string) =>
            set((state) => {
              delete state.forms[formId];
            }),

          // Clear all forms
          clearAllForms: () =>
            set((state) => {
              state.forms = {};
            }),

          setFormIdentifier: (identifier: string, value: string) =>
            set((state) => {
              state.formIdentifier[identifier] = value;
            }),
          getFormIdentifier: (identifier: string) => {
            return get().formIdentifier[identifier];
          },
        })),

        {
          name: 'xemplo-form-store',
          storage: createJSONStorage(() => sessionStorage),
          partialize: (state) => ({
            forms: Object.fromEntries(
              Object.entries(state.forms).map(([formId, form]) => [
                formId,
                { values: form.values },
              ])
            ),
            formIdentifier: Object.fromEntries(
              Object.entries(state.formIdentifier).map(([identifier, value]) => [
                identifier,
                value,
              ])
            ),
          }),
        }
      )
    )
  )
);

// The following hooks are purely for convenience. You can just use the main useFormSyncStore hook instead

// Hook to get form values for a specific form
export const useFormValues = <T,>(formId: string) => {
  return useXemploFormStore((state) => state.forms[formId]?.values as T | undefined);
};

// Hook to get the submit function for a specific form
export const useFormSubmit = (formId: string) => {
  return useXemploFormStore((state) => state.forms[formId]?.submit);
};

// Hook to update form values from outside
export const useFormUpdater = (formId: string) => {
  const { setFormValues, setFormField } = useXemploFormStore();

  return {
    setValues: (values: FieldValues) => setFormValues(formId, values),
    setField: (field: string, value: any) => setFormField(formId, field, value),
  };
};

// Hook to clear all forms
export const useClearAllForms = () => {
  return useXemploFormStore((state) => state.clearAllForms);
};
